import { clientSDK } from "@istari-frontend/api-client";
import { getResponseStatus } from "@istari-frontend/shared";
import { UpdateTag } from "@istari/istari-client";
import type {
  ArchiveStatus as ApiArchiveStatus,
  NewSnapshot,
  NewSnapshotTag,
  ResponseCreateSnapshot,
  Snapshot,
  SnapshotTag,
} from "@istari/istari-client";

import { toBranch } from "~/branching/data-source/adapter/to-branch";
import { toBranchHeadCommitMeta } from "~/branching/data-source/adapter/to-branch-head-commit-meta";
import { toBranchListItem } from "~/branching/data-source/adapter/to-branch-list-item";
import { toBranchingError } from "~/branching/data-source/adapter/to-branching-error";
import { type CommitSnapshotSource, toCommit } from "~/branching/data-source/adapter/to-commit";
import { toCommitHistoryEntries } from "~/branching/data-source/adapter/to-commit-history-entries";
import type { ArchiveStatus } from "~/branching/model/archive-status";
import type { Branch, BranchId, BranchSummary } from "~/branching/model/branch";
import type { BranchHeadCommitMeta } from "~/branching/model/branch-head-commit-meta";
import { BranchingError } from "~/branching/model/branching-error";
import type { Commit, CommitId } from "~/branching/model/commit";
import type { CommitHistoryEntry } from "~/branching/model/commit-history-entry";

import type { CreateBranchSource, ListBranchesResult, ListBranchItemsResult } from "./branching-gateway";
import type { BranchingGateway } from "./branching-gateway";

const PAGE_SIZE = 100;

async function listAllTags(systemId: string): Promise<SnapshotTag[]> {
  const firstPage = await clientSDK.listTags(systemId, null, null, "all", 1, PAGE_SIZE);
  const allTags: SnapshotTag[] = [...firstPage.items];
  if (firstPage.total && firstPage.total > PAGE_SIZE) {
    const totalPages = Math.ceil(firstPage.total / PAGE_SIZE);
    const more = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        clientSDK.listTags(systemId, null, null, "all", i + 2, PAGE_SIZE),
      ),
    );
    for (const p of more) {
      allTags.push(...p.items);
    }
  }
  return allTags;
}

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (cause) {
    throw await toBranchingError(cause);
  }
}

function snapshotToCommitSource(head: Snapshot): CommitSnapshotSource {
  const extended = head as Snapshot & { configurationId?: string; message?: string | null };
  return {
    id: head.id,
    created: head.created,
    createdById: head.createdById ?? null,
    message: extended.message,
    configurationId: extended.configurationId ?? null,
  };
}

type TagRevisionHeadSource = {
  id: string;
  created?: string | Date | null;
  createdById?: string | null;
};

function mergeHeadCommitSource(
  snapshotSource: CommitSnapshotSource,
  revisionSource?: TagRevisionHeadSource,
): CommitSnapshotSource {
  if (!revisionSource) {
    return snapshotSource;
  }

  return {
    ...snapshotSource,
    created: revisionSource.created === undefined ? snapshotSource.created : revisionSource.created,
    createdById: revisionSource.createdById === undefined ? snapshotSource.createdById : revisionSource.createdById,
  };
}

async function getDefaultBranchId(systemId: string): Promise<BranchId | null> {
  const baseline = await clientSDK.getSystemBaseline(systemId).catch(async (cause) => {
    if (getResponseStatus(cause) === 404) {
      return null;
    }
    throw await toBranchingError(cause);
  });
  return baseline ? (baseline.tagId as BranchId) : null;
}

async function toBranchFromTag(tag: SnapshotTag, systemId: string): Promise<Branch> {
  const head = await clientSDK.getSnapshot(tag.snapshotId);
  const defaultBranchId = await getDefaultBranchId(systemId);
  return toBranch(tag, snapshotToCommitSource(head), defaultBranchId);
}

export class HttpBranchingGateway implements BranchingGateway {
  async listBranches(systemId: string): Promise<ListBranchesResult> {
    return wrap(async () => {
      const defaultBranchId = await getDefaultBranchId(systemId);
      const tags = await listAllTags(systemId);
      const bySnapshot = new Map<string, Promise<Snapshot>>();
      for (const tag of tags) {
        if (!bySnapshot.has(tag.snapshotId)) {
          bySnapshot.set(tag.snapshotId, clientSDK.getSnapshot(tag.snapshotId));
        }
      }
      const snapshotResults = await Promise.all([...bySnapshot.values()]);
      const shotById = new Map<string, Snapshot>();
      for (const s of snapshotResults) {
        shotById.set(s.id, s);
      }
      const revisionHeads = new Map<string, TagRevisionHeadSource>();
      // No bulk tag-history endpoint exists yet; branch count is expected to stay low during the gated MVP.
      const tagHeadEntries = await Promise.all(
        tags.map(async (tag): Promise<[string, TagRevisionHeadSource | undefined]> => {
          const revisions = await clientSDK.getTagHistory(tag.id);
          const latest = revisions[0];
          return latest
            ? [
                tag.id,
                {
                  id: latest.id,
                  created: latest.created,
                  createdById: latest.createdById ?? null,
                },
              ]
            : [tag.id, undefined];
        }),
      );
      for (const [tagId, head] of tagHeadEntries) {
        if (head) {
          revisionHeads.set(tagId, head);
        }
      }
      const branches: Branch[] = tags.map((tag) => {
        const head = shotById.get(tag.snapshotId);
        if (!head) {
          throw new BranchingError("unknown", `Missing head snapshot for branch ${tag.id}`);
        }
        const revisionHead = revisionHeads.get(tag.id);
        const headSource = mergeHeadCommitSource(snapshotToCommitSource(head), revisionHead);
        return toBranch(tag, headSource, defaultBranchId, revisionHead?.id);
      });
      return { branches, defaultBranchId };
    });
  }

  async listBranchItems(
    systemId: string,
    archiveStatuses: ArchiveStatus[],
    pageIndex: number,
    pageSize: number,
  ): Promise<ListBranchItemsResult> {
    if (pageIndex < 0 || pageSize <= 0) {
      throw new BranchingError("invalid", `Invalid pagination: pageIndex=${pageIndex}, pageSize=${pageSize}`);
    }
    return wrap(async () => {
      // Identity list only. `listTags` carries name/snapshot/baseline/archive per tag, so no
      // per-branch snapshot or baseline lookup. `listTags` pages are 1-based; the port is 0-based.
      const page = await clientSDK.listTags(
        systemId,
        null,
        null,
        toApiArchiveStatus(archiveStatuses),
        pageIndex + 1,
        pageSize,
      );
      const items = page.items.map(toBranchListItem);
      const nextPageIndex = (pageIndex + 1) * pageSize < page.total ? pageIndex + 1 : undefined;
      return { items, nextPageIndex, total: page.total };
    });
  }

  async getBranchHeadCommitMeta(branchId: BranchId): Promise<BranchHeadCommitMeta> {
    return wrap(async () => {
      const revisions = await clientSDK.getTagHistory(branchId);
      const meta = toBranchHeadCommitMeta(revisions);
      if (!meta) {
        throw new BranchingError("not-found", `No revision history for branch ${branchId}`);
      }
      return meta;
    });
  }

  async getCommitHistory(branchId: BranchId): Promise<CommitHistoryEntry[]> {
    return wrap(async () => {
      const revisions = await clientSDK.getTagHistory(branchId);
      return toCommitHistoryEntries(revisions);
    });
  }

  async getBranchSummary(branchId: BranchId): Promise<BranchSummary> {
    return wrap(async () => {
      // listBranches omits archived tags; fetch directly so a CR's archived source/target branch still has a name.
      const tag = await clientSDK.getTag(branchId);
      return { id: tag.id as BranchId, name: tag.tag, headCommitId: tag.snapshotId as CommitId };
    });
  }

  async createCommit(input: { configId: string; message?: string }): Promise<Commit> {
    return wrap(async () => {
      const body = {
        dryRun: false,
        ...(input.message !== undefined && input.message.length > 0 ? { message: input.message } : {}),
      } as NewSnapshot;
      const response = (await clientSDK.createSnapshot(input.configId, body)) as ResponseCreateSnapshot;
      return toCommit({
        ...(response as CommitSnapshotSource),
        configurationId: input.configId,
      });
    });
  }

  async createBranch(input: { systemId: string; source: CreateBranchSource; name: string }): Promise<Branch> {
    return wrap(async () => {
      const tags = await listAllTags(input.systemId);
      const source = input.source;
      let sourceSnapshotId: string | undefined;
      if (source.kind === "snapshot") {
        sourceSnapshotId = source.snapshotId;
      } else {
        sourceSnapshotId = tags.find((tag) => tag.id === source.branchId)?.snapshotId;
      }
      if (!sourceSnapshotId) {
        throw new BranchingError("not-found", "Source branch not found");
      }
      const created = await clientSDK.createTag(sourceSnapshotId, { tag: input.name } as NewSnapshotTag);
      return toBranchFromTag(created, input.systemId);
    });
  }

  async renameBranch(input: { systemId: string; branchId: BranchId; name: string }): Promise<Branch> {
    return wrap(async () => {
      const updated = await clientSDK.updateTag(input.branchId, new UpdateTag(undefined, undefined, input.name));
      return toBranchFromTag(updated as SnapshotTag, input.systemId);
    });
  }

  async moveBranchHead(input: { systemId: string; branchId: BranchId; toCommitId: CommitId }): Promise<Branch> {
    return wrap(async () => {
      const updated = await clientSDK.updateTag(input.branchId, new UpdateTag(input.toCommitId as string));
      return toBranchFromTag(updated as SnapshotTag, input.systemId);
    });
  }

  async fastForwardDefaultBranch(input: { systemId: string; branchId: BranchId }): Promise<Branch> {
    return wrap(async () => {
      const baseline = await clientSDK.getSystemBaseline(input.systemId);
      if (!baseline) {
        throw new BranchingError("not-found", "No baseline for system");
      }
      const tags = await listAllTags(input.systemId);
      const target = tags.find((t) => t.id === input.branchId);
      if (!target) {
        throw new BranchingError("not-found", "Branch not found");
      }
      await clientSDK.updateTag(baseline.tagId, new UpdateTag(target.snapshotId));
      const head = await clientSDK.getSnapshot(target.snapshotId);
      return toBranch(
        { ...target, id: baseline.tagId, tag: target.tag, snapshotId: target.snapshotId },
        snapshotToCommitSource(head),
        baseline.tagId as BranchId,
      );
    });
  }

  async archiveBranch(branchId: BranchId): Promise<void> {
    await wrap(() => clientSDK.archiveTag(branchId));
  }

  async restoreBranch(branchId: BranchId): Promise<void> {
    await wrap(() => clientSDK.restoreTag(branchId));
  }
}

function toApiArchiveStatus(statuses: ArchiveStatus[]): ApiArchiveStatus {
  if (statuses.length === 0) return "all";
  const wantsActive = statuses.includes("active");
  const wantsArchived = statuses.includes("archived");
  if (wantsActive && wantsArchived) return "all";
  return wantsArchived ? "archived" : "active";
}
